import {
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useMemo,
	useRef
} from "react";
import { useUnmountedCancellation } from "./useUnmountedCancellation";

export type PromiseQueueModel = {
	addTask: (task: (signal: AbortSignal) => Promise<any>) => Promise<any>;
};

export function usePromiseQueue(): PromiseQueueModel {
	const isProcessingRef: RefObject<boolean> = useRef<boolean>(false);
	const queueRef: ReadonlyRefObject<(() => Promise<any>)[]> = useRef<(() => Promise<any>)[]>([]);

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const processQueue = useCallback(
		async (): Promise<void> => {
			if (isProcessingRef.current) {
				return;
			}

			isProcessingRef.current = true;

			while (queueRef.current.length > 0) {
				const currentTask: (() => Promise<any>) | undefined = queueRef.current
					.shift();

				if (currentTask) {
					try {
						await currentTask();
					} catch {
						// Nothing to do
					}
				}
			}

			if (!globalCancellation.aborted) {
				isProcessingRef.current = false;
			}
		},
		[
			isProcessingRef,
			globalCancellation
		]
	);

	const addTask = useCallback(
		<TModel,>(task: (signal: AbortSignal) => Promise<TModel>): Promise<TModel> => {
			const result = new Promise<TModel>((resolve, reject) => {
				queueRef
					.current
					.push(
						() => task(globalCancellation)
							.then(resolve)
							.catch(reject)
					);
			});

			processQueue();

			return result;
		},
		[
			processQueue,
			globalCancellation
		]
	);

	return useMemo(
		() => ({
			addTask
		}),
		[addTask]
	);
};